import { useMemo, type ReactNode } from 'react'
import { SearchableSelect } from '@/components/pedagogy/SearchableSelect'
import type {
  PedagogyPrioritySessionOption,
  PedagogyPriorityTeacherOption,
} from '@/services/pedagogyPriorityRequestService'

export interface PriorityReviewRequestFilters {
  teacherId: string
  studioInternalId: string
  directionName: string
  trainingDate: string
  itemId: string
  deferredActivation: boolean
  availableFrom: string
}

function uniqueByValue(items: Array<{ value: string; label: string; description?: string }>) {
  const seen = new Set<string>()
  return items.filter(item => {
    if (!item.value || seen.has(item.value)) return false
    seen.add(item.value)
    return true
  })
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-')
  if (!year || !month || !day) return value
  return `${day}.${month}.${year}`
}

function formatTime(value: string): string {
  return value.slice(0, 5)
}

function localIsoDate(): string {
  const now = new Date()
  const offset = now.getTimezoneOffset()
  return new Date(now.getTime() - offset * 60_000).toISOString().slice(0, 10)
}

export function PriorityReviewRequestFields({
  mode,
  sessions,
  teacherOptions,
  value,
  onChange,
}: {
  mode: 'partner' | 'lead'
  sessions: PedagogyPrioritySessionOption[]
  teacherOptions?: PedagogyPriorityTeacherOption[]
  value: PriorityReviewRequestFilters
  onChange: (value: PriorityReviewRequestFilters) => void
}) {
  const teacherSelectOptions = useMemo(() => {
    if (teacherOptions) {
      return teacherOptions.map(teacher => ({
        value: teacher.teacherId,
        label: teacher.fullName,
        description: teacher.status === 'training' ? 'Обучение' : 'Работает',
      }))
    }

    return uniqueByValue(sessions
      .filter(session => session.teacherId && session.teacherName)
      .map(session => ({
        value: session.teacherId ?? '',
        label: session.teacherName ?? '',
      })))
      .sort((left, right) => left.label.localeCompare(right.label, 'ru'))
  }, [sessions, teacherOptions])

  const sessionsForStudio = useMemo(() => {
    if (mode === 'partner') {
      return sessions.filter(session => !value.teacherId || session.teacherId === value.teacherId)
        .filter(session => !value.directionName || session.directionName === value.directionName)
    }
    return sessions
  }, [mode, sessions, value.directionName, value.teacherId])

  const studioOptions = useMemo(() => uniqueByValue(sessionsForStudio.map(session => ({
    value: String(session.studioInternalId),
    label: session.studioAddress || `Студия №${session.studioNumber}`,
    description: session.cityName || undefined,
  }))).sort((left, right) => left.label.localeCompare(right.label, 'ru')), [sessionsForStudio])

  const sessionsForTeacher = useMemo(() => {
    if (mode === 'partner') return sessions
    return sessions.filter(session => !value.studioInternalId || String(session.studioInternalId) === value.studioInternalId)
  }, [mode, sessions, value.studioInternalId])

  const leadTeacherOptions = useMemo(() => uniqueByValue(sessionsForTeacher
    .filter(session => session.teacherId && session.teacherName)
    .map(session => ({
      value: session.teacherId ?? '',
      label: session.teacherName ?? '',
    }))).sort((left, right) => left.label.localeCompare(right.label, 'ru')), [sessionsForTeacher])

  const effectiveTeacherOptions = mode === 'partner' ? teacherSelectOptions : leadTeacherOptions

  const directionOptions = useMemo(() => {
    const base = sessions
      .filter(session => !value.teacherId || session.teacherId === value.teacherId)
      .filter(session => !value.studioInternalId || String(session.studioInternalId) === value.studioInternalId)
    return [...new Set(base.map(session => session.directionName).filter((item): item is string => Boolean(item)))]
      .sort((left, right) => left.localeCompare(right, 'ru'))
  }, [sessions, value.studioInternalId, value.teacherId])

  const directionSelectOptions = useMemo(() => [
    { value: '', label: 'Любое направление' },
    ...directionOptions.map(direction => ({ value: direction, label: direction })),
  ], [directionOptions])

  const dateOptions = useMemo(() => {
    const base = sessions
      .filter(session => !value.teacherId || session.teacherId === value.teacherId)
      .filter(session => !value.studioInternalId || String(session.studioInternalId) === value.studioInternalId)
      .filter(session => !value.directionName || session.directionName === value.directionName)
    return [...new Set(base.map(session => session.trainingDate))]
      .sort((left, right) => right.localeCompare(left))
  }, [sessions, value.directionName, value.studioInternalId, value.teacherId])

  const concreteSessions = useMemo(() => sessions
    .filter(session => !value.teacherId || session.teacherId === value.teacherId)
    .filter(session => !value.studioInternalId || String(session.studioInternalId) === value.studioInternalId)
    .filter(session => !value.directionName || session.directionName === value.directionName)
    .filter(session => !value.trainingDate || session.trainingDate === value.trainingDate), [
      sessions,
      value.directionName,
      value.studioInternalId,
      value.teacherId,
      value.trainingDate,
    ])

  const showSessionSelectors = mode === 'partner' || !value.deferredActivation

  const changeTeacher = (teacherId: string) => {
    if (mode === 'partner') {
      onChange({ teacherId, directionName: '', studioInternalId: '', trainingDate: '', itemId: '', availableFrom: value.availableFrom, deferredActivation: false })
      return
    }
    onChange({ ...value, teacherId, directionName: '', trainingDate: '', itemId: '' })
  }

  const changeStudio = (studioInternalId: string) => {
    if (mode === 'lead') {
      onChange({ teacherId: '', studioInternalId, directionName: '', trainingDate: '', itemId: '', availableFrom: value.availableFrom, deferredActivation: value.deferredActivation })
      return
    }
    onChange({ ...value, studioInternalId, trainingDate: '', itemId: '' })
  }

  const changeDirection = (directionName: string) => {
    onChange({
      ...value,
      directionName,
      studioInternalId: mode === 'partner' ? '' : value.studioInternalId,
      trainingDate: '',
      itemId: '',
    })
  }

  const teacherField = (
    <Field label={mode === 'partner' ? 'Педагог' : 'Педагог · необязательно'}>
      <SearchableSelect
        value={value.teacherId}
        options={effectiveTeacherOptions}
        onChange={changeTeacher}
        placeholder={mode === 'partner' ? 'Выберите педагога' : 'Любой педагог'}
        searchPlaceholder="Найти педагога"
        emptyMessage="Подходящих педагогов нет"
      />
      {mode === 'lead' && value.teacherId && (
        <ClearButton onClick={() => changeTeacher('')}>Любой педагог</ClearButton>
      )}
    </Field>
  )

  const studioField = (
    <Field label="Студия · необязательно">
      <SearchableSelect
        value={value.studioInternalId}
        options={studioOptions}
        onChange={changeStudio}
        placeholder="Любая студия"
        searchPlaceholder="Найти студию"
        emptyMessage="Подходящих студий нет"
      />
      {value.studioInternalId && (
        <ClearButton onClick={() => changeStudio('')}>Любая студия</ClearButton>
      )}
    </Field>
  )

  const directionField = (
    <Field label="Направление · необязательно">
      <SearchableSelect
        value={value.directionName}
        options={directionSelectOptions}
        onChange={changeDirection}
        placeholder="Любое направление"
        searchPlaceholder="Найти направление"
        emptyMessage="Подходящих направлений нет"
        disabled={mode === 'partner' && !value.teacherId}
        matchTriggerWidth
      />
    </Field>
  )

  return (
    <div className="space-y-4">
      {mode === 'lead' && (
        <div className="space-y-2 rounded-lg border border-border bg-muted/20 px-3 py-2.5">
          <label className="flex cursor-pointer items-start gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={value.deferredActivation}
              onChange={event => onChange({
                ...value,
                deferredActivation: event.target.checked,
                availableFrom: event.target.checked ? value.availableFrom : '',
                trainingDate: event.target.checked ? '' : value.trainingDate,
                itemId: event.target.checked ? '' : value.itemId,
              })}
            />
            <span className="font-medium">Отложить запрос</span>
          </label>
          {value.deferredActivation && (
            <div className="space-y-1.5 pl-6">
              <div className="text-xs font-medium text-muted-foreground">Начать поиск с даты</div>
              <input
                type="date"
                className="input w-full"
                min={localIsoDate()}
                value={value.availableFrom}
                onChange={event => onChange({ ...value, availableFrom: event.target.value })}
              />
              <div className="text-xs text-muted-foreground">С этой даты подходящее занятие начнёт попадать в очередь.</div>
            </div>
          )}
        </div>
      )}

      {mode === 'partner' ? (
        <>
          {teacherField}
          {directionField}
          {studioField}
        </>
      ) : (
        <>
          {studioField}
          {teacherField}
          {directionField}
        </>
      )}

      {showSessionSelectors && (
        <Field label="Дата · необязательно">
          <select
            className="input w-full"
            value={value.trainingDate}
            disabled={mode === 'partner' && !value.teacherId}
            onChange={event => onChange({ ...value, trainingDate: event.target.value, itemId: '' })}
          >
            <option value="">Любая дата</option>
            {dateOptions.map(date => <option key={date} value={date}>{formatDate(date)}</option>)}
          </select>
        </Field>
      )}

      {showSessionSelectors && (
        <Field label="Конкретная тренировка · необязательно">
          <select
            className="input w-full"
            value={value.itemId}
            disabled={!value.trainingDate}
            onChange={event => onChange({ ...value, itemId: event.target.value })}
          >
            <option value="">Любая тренировка в выбранных условиях</option>
            {concreteSessions.map(session => (
              <option key={session.itemId} value={String(session.itemId)}>
                {formatTime(session.trainingTime)} · {mode === 'lead' ? `${session.teacherName || 'педагог не определён'} · ` : ''}{session.directionName || 'направление не указано'} · {session.studioAddress || `студия №${session.studioNumber}`}
              </option>
            ))}
          </select>
        </Field>
      )}

      {mode === 'partner' && !value.teacherId && (
        <div className="text-xs text-muted-foreground">Сначала выберите педагога.</div>
      )}
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {children}
    </div>
  )
}

function ClearButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="mt-1 text-xs font-medium text-primary hover:underline">
      {children}
    </button>
  )
}
